/**
 * The pure view model for the pool Settings Awards card. Turns one
 * `/award/custom-list` envelope, which returns active and archived rows
 * together with no `status` filter param, into the two active lists, the
 * two counters, the two Create-enabled flags, and the single shared
 * Archived list with each row's restore-blocked flag.
 */
import type { typeAwardKind } from "./poolAwardsCopy";

/**
 * A server-side literal, not a served field. `/award/custom-list` serves no
 * cap field. This must stay equal to the cap enforced at
 * `services/contest-api/src/repo/awardTypes.ts:114`.
 */
export const ACTIVE_TYPE_CAP = 5;

export type typeAwardTypeRow = {
  awardTypeId: string;
  name: string;
  color: string;
  status: "active" | "archived";
  grantCount: number;
  memberCount: number;
};

export type typeAwardKindSection = {
  kind: typeAwardKind;
  activeRows: readonly typeAwardTypeRow[];
  activeCount: number;
  canCreate: boolean;
};

export type typeArchivedAwardRow = {
  row: typeAwardTypeRow;
  kind: typeAwardKind;
  canRestore: boolean;
};

export type typePoolAwardsModel = {
  medals: typeAwardKindSection;
  trophies: typeAwardKindSection;
  archived: readonly typeArchivedAwardRow[];
  hasArchived: boolean;
};

function sectionFor(
  kind: typeAwardKind,
  rows: readonly typeAwardTypeRow[],
): typeAwardKindSection {
  const activeRows = rows.filter((row) => row.status === "active");
  const activeCount = activeRows.length;
  return {
    kind,
    activeRows,
    activeCount,
    canCreate: activeCount < ACTIVE_TYPE_CAP,
  };
}

function archivedFor(
  kind: typeAwardKind,
  rows: readonly typeAwardTypeRow[],
  activeCount: number,
): typeArchivedAwardRow[] {
  return rows
    .filter((row) => row.status === "archived")
    .map((row) => ({
      row,
      kind,
      canRestore: activeCount < ACTIVE_TYPE_CAP,
    }));
}

export function buildPoolAwardsModel(input: {
  medals: readonly typeAwardTypeRow[];
  trophies: readonly typeAwardTypeRow[];
}): typePoolAwardsModel {
  const medals = sectionFor("medal", input.medals);
  const trophies = sectionFor("trophy", input.trophies);
  const archivedMedals = archivedFor("medal", input.medals, medals.activeCount);
  const archivedTrophies = archivedFor(
    "trophy",
    input.trophies,
    trophies.activeCount,
  );
  const archived = [...archivedMedals, ...archivedTrophies];

  return {
    medals,
    trophies,
    archived,
    hasArchived: archived.length > 0,
  };
}

export function isChooserNeeded(row: typeAwardTypeRow): boolean {
  return row.grantCount > 0;
}
